import { useState, type ComponentProps, type MouseEvent, type ReactNode } from 'react';
import { cn } from './cn';
import { useFieldControl } from './Field';

/** Box styles shared by text-like controls (inputs, select and picker triggers). */
export const controlBox = cn(
  'flex h-8 w-full min-w-0 items-center gap-2 rounded-md border border-line-strong bg-surface px-2.5 text-sm text-ink shadow-xs transition-colors pointer-coarse:h-10',
  'hover:border-ink-faint focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/25',
  'has-[[aria-invalid=true]]:border-danger has-[[aria-invalid=true]]:focus-within:ring-danger/20',
  'has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-subtle has-[:disabled]:opacity-60',
);

/** The bare <input> inside a control box. */
const bareInput =
  'm-0 h-full w-full min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-sm text-ink outline-none placeholder:text-ink-faint disabled:cursor-not-allowed pointer-coarse:text-[16px]';

export interface InputProps extends Omit<ComponentProps<'input'>, 'prefix' | 'size'> {
  /** Inside the box, before the text (an icon or a unit such as "$"). */
  prefix?: ReactNode;
  /** Inside the box, after the text. */
  suffix?: ReactNode;
  /** Classes for the outer box (width etc.); `className` styles the <input>. */
  boxClassName?: string;
}

/**
 * A tap on the box outside the text (padding, an icon) focuses the input, inside the tap's own
 * gesture, so phones open the keyboard; a suffix button keeps its own click.
 */
function focusInput(e: MouseEvent<HTMLDivElement>) {
  const target = e.target as HTMLElement;
  if (target.closest('input, button, a, [role="button"]')) return;
  e.currentTarget.querySelector('input')?.focus();
}

/** Single-line text input with optional prefix/suffix adornments. */
export function Input({ prefix, suffix, boxClassName, className, ...props }: InputProps) {
  const field = useFieldControl(props);
  return (
    <div className={cn(controlBox, 'cursor-text', boxClassName)} onClick={focusInput}>
      {prefix && <span className="flex shrink-0 items-center text-ink-faint [&_svg]:size-4">{prefix}</span>}
      <input {...props} {...field} className={cn(bareInput, className)} />
      {suffix && <span className="flex shrink-0 items-center text-ink-faint [&_svg]:size-4">{suffix}</span>}
    </div>
  );
}

export interface NumberInputProps extends Omit<InputProps, 'value' | 'defaultValue' | 'onChange' | 'type' | 'min' | 'max'> {
  value: number | null | undefined;
  onChange(value: number | null): void;
  min?: number;
  max?: number;
  /** Decimal places: input is rounded to it on blur and the value is shown with it (2 for money). */
  precision?: number;
}

function formatNumber(value: number | null | undefined, precision?: number): string {
  if (value == null || !Number.isFinite(value)) return '';
  return precision == null ? String(value) : value.toFixed(precision);
}

/**
 * Numeric input. Reports a number (or null when empty) on every keystroke; on blur the value is
 * rounded to `precision` and clamped to [min, max]. A text input underneath, so the browser never
 * swallows partial input like "1." or "-".
 */
export function NumberInput({ value, onChange, min, max, precision, onBlur, onFocus, ...props }: NumberInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? formatNumber(value, precision);

  const settle = (n: number | null): number | null => {
    if (n === null) return null;
    let v = precision == null ? n : Number(n.toFixed(precision));
    if (min != null) v = Math.max(min, v);
    if (max != null) v = Math.min(max, v);
    return v;
  };

  return (
    <Input
      inputMode={precision === 0 ? 'numeric' : 'decimal'}
      autoComplete="off"
      {...props}
      value={shown}
      onFocus={(e) => {
        setDraft(formatNumber(value, precision));
        onFocus?.(e);
      }}
      onChange={(e) => {
        const text = e.target.value;
        if (!/^-?\d*\.?\d*$/.test(text)) return;
        setDraft(text);
        const n = text === '' || text === '-' || text === '.' ? null : Number(text);
        onChange(n !== null && Number.isFinite(n) ? n : null);
      }}
      onBlur={(e) => {
        const settled = settle(value ?? null);
        if (settled !== (value ?? null)) onChange(settled);
        setDraft(null);
        onBlur?.(e);
      }}
    />
  );
}

/** Multi-line text input; `showCount` with `maxLength` shows the characters used. */
export function Textarea({ className, showCount, ...props }: ComponentProps<'textarea'> & { showCount?: boolean }) {
  const field = useFieldControl(props);
  const length = typeof props.value === 'string' ? props.value.length : 0;
  return (
    <div className="relative">
      <textarea
        rows={3}
        {...props}
        {...field}
        className={cn(
          'm-0 block w-full min-w-0 resize-y rounded-md border border-line-strong bg-surface px-2.5 py-1.5 font-sans text-sm text-ink shadow-xs outline-none transition-colors pointer-coarse:text-[16px]',
          'placeholder:text-ink-faint hover:border-ink-faint focus:border-ring focus:ring-2 focus:ring-ring/25 aria-[invalid=true]:border-danger',
          showCount && props.maxLength && 'pb-5',
          className,
        )}
      />
      {showCount && props.maxLength && (
        <span className="pointer-events-none absolute bottom-1 right-2.5 text-2xs text-ink-faint" aria-hidden>
          {length}/{props.maxLength}
        </span>
      )}
    </div>
  );
}
